import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Search, Edit2, Trash2, Ban, ShieldOff, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.data?.users || response.data.data || []);
      setSelected(new Set());
    } catch (error) {
      console.error(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/status`, { kycStatus: status });
      fetchUsers();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleAccountStatus = async (id, status) => {
    const verbs = { SUSPENDED: 'suspend', BANNED: 'ban', ACTIVE: 'reactivate' };
    let reason = null;
    if (status !== 'ACTIVE') {
      reason = window.prompt(`Reason for ${verbs[status]}ing this user (optional, shown to them on login):`);
      if (reason === null) return; // cancelled
    } else {
      if (!window.confirm('Reactivate this account?')) return;
    }
    try {
      await api.put(`/admin/users/${id}/account-status`, { status, reason });
      fetchUsers();
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error?.message || err.message}`);
    }
  };

  // Filter logic shared by table + select-all
  const filtered = useMemo(() => users
    .filter(u => !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
    .filter(u => !kycFilter || u.kycStatus === kycFilter),
    [users, search, kycFilter]
  );

  // Only non-admins are selectable
  const selectableIds = useMemo(
    () => filtered.filter(u => !u.isAdmin && u.email !== 'admin@cirupay.com').map(u => u.id),
    [filtered]
  );

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selectableIds.some(id => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const proceed = window.confirm(
      `Delete ${ids.length} user${ids.length > 1 ? 's' : ''}? This will also wipe every circle they created and all their transactions, KYC, bank accounts, and notifications. This cannot be undone.`
    );
    if (!proceed) return;

    setDeleting(true);
    try {
      const res = await api.post('/admin/users/bulk-delete', { userIds: ids });
      const { deletedUsers, deletedCircles, skippedAdmins } = res.data.data || res.data;
      alert(`Deleted ${deletedUsers} user(s) and ${deletedCircles} circle(s).${skippedAdmins ? ` Skipped ${skippedAdmins} admin account(s).` : ''}`);
      await fetchUsers();
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.error?.message || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="card-header" style={{ padding: '0 0 24px 0', border: 'none' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Users Management</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <button
              className="btn-danger"
              onClick={handleBulkDelete}
              disabled={deleting}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Trash2 size={16} />
              {deleting ? 'Deleting…' : `Delete Selected (${selected.size})`}
            </button>
          )}
          <button className="btn-primary">Export CSV</button>
        </div>
      </div>

      <div className="glass">
        <div className="card-header">
          <div className="action-bar" style={{ margin: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
              <input
                type="text"
                placeholder="Search users..."
                className="search-input"
                style={{ paddingLeft: 40 }}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="dropdown" value={kycFilter} onChange={e => setKycFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ border: 'none', borderRadius: '0 0 16px 16px' }}>
          {loading ? (
            <div className="loader-container"><div className="spinner"></div></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                      onChange={toggleAll}
                      disabled={selectableIds.length === 0}
                      title="Select all (non-admins)"
                    />
                  </th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Joined Date</th>
                  <th>KYC</th>
                  <th>Account</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => {
                  const isAdminRow = user.isAdmin || user.email === 'admin@cirupay.com';
                  return (
                    <tr key={user.id || user._id} style={selected.has(user.id) ? { background: 'rgba(239, 68, 68, 0.08)' } : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(user.id)}
                          onChange={() => toggleOne(user.id)}
                          disabled={isAdminRow}
                          title={isAdminRow ? 'Admins cannot be deleted' : ''}
                        />
                      </td>
                      <td>
                        <Link to={`/users/${user.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'inherit', textDecoration: 'none' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontWeight: 600 }}>
                            {(user.firstName?.[0] || '') + (user.lastName?.[0] || '')}
                          </div>
                          <div style={{ fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                            {isAdminRow && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.2)', color: 'var(--accent)' }}>ADMIN</span>}
                            {user.flagged && <span style={{ marginLeft: 6, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}>FLAGGED</span>}
                          </div>
                        </Link>
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{user.email}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {user.createdAt ? format(new Date(user.createdAt), 'MMM dd, yyyy') : 'N/A'}
                      </td>
                      <td>
                        <span className={`badge ${(user.kycStatus || 'pending').toLowerCase()}`}>
                          {user.kycStatus || 'PENDING'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${(user.accountStatus || 'active').toLowerCase()}`}
                              style={user.accountStatus === 'BANNED' ? { background: 'rgba(239,68,68,0.2)', color: '#fca5a5' } :
                                     user.accountStatus === 'SUSPENDED' ? { background: 'rgba(245,158,11,0.2)', color: '#fcd34d' } : undefined}>
                          {user.accountStatus || 'ACTIVE'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {user.kycStatus === 'PENDING' && !isAdminRow && (
                            <>
                              <button className="btn-success" onClick={() => handleUpdateStatus(user.id, 'APPROVED')}>Approve</button>
                              <button className="btn-danger" onClick={() => handleUpdateStatus(user.id, 'REJECTED')}>Reject</button>
                            </>
                          )}
                          {!isAdminRow && user.accountStatus !== 'SUSPENDED' && user.accountStatus !== 'BANNED' && (
                            <button className="btn-secondary" title="Suspend"
                                    onClick={() => handleAccountStatus(user.id, 'SUSPENDED')}
                                    style={{ padding: '6px 10px' }}>
                              <ShieldOff size={14} />
                            </button>
                          )}
                          {!isAdminRow && user.accountStatus !== 'BANNED' && (
                            <button className="btn-danger" title="Ban"
                                    onClick={() => handleAccountStatus(user.id, 'BANNED')}
                                    style={{ padding: '6px 10px' }}>
                              <Ban size={14} />
                            </button>
                          )}
                          {!isAdminRow && (user.accountStatus === 'SUSPENDED' || user.accountStatus === 'BANNED') && (
                            <button className="btn-success" title="Reactivate"
                                    onClick={() => handleAccountStatus(user.id, 'ACTIVE')}
                                    style={{ padding: '6px 10px' }}>
                              <ShieldCheck size={14} />
                            </button>
                          )}
                          <button className="btn-secondary" style={{ padding: '6px 10px' }} title="Edit"><Edit2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
